import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatIconModule } from "@angular/material/icon";
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  OverlayModule,
  ConnectedPosition,
} from "@angular/cdk/overlay";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

import { PlanningEvent } from "../../../../features/planning/models/planning-event.model";
import { PlanningCell } from "../../../../features/planning/models/planning-cell.model";
import { isOffDay } from "../../../../features/planning/utils/french-holidays";

@Component({
  selector: "vl-planning-list-cell",
  standalone: true,
  imports: [CommonModule, MatTooltipModule, MatIconModule, OverlayModule],
  templateUrl: "./vl-planning-list-cell.component.html",
  styleUrls: ["./vl-planning-list-cell.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // Applique `.vl-planning-cell--empty` sur l'élément hôte quand la cellule
    // n'a aucun event → CSS la compacte (cf. scss :host(.vl-planning-cell--empty)).
    "[class.vl-planning-cell--empty]": "cell().events.length === 0",
    // Stretch : toutes les cellules voisines sont vides → autoriser les empties
    // à remplir la largeur au lieu de rester compactées à 68px.
    "[class.vl-planning-cell--stretch]": "stretchEmpty() && cell().events.length === 0",
    // Weekend ou jour férié français → cellule grisée (les events y sont quand même affichés).
    "[class.vl-planning-cell--off]": "isCellOffDay()",
  },
})
export class VlPlanningListCellComponent
  implements AfterViewInit, OnDestroy
{
  private host = inject(ElementRef<HTMLElement>);
  cell = input.required<PlanningCell>();
  comfortable = input<boolean>(true);
  maxVisible = input<number>(3);
  /** Si true, les cellules vides prennent toute la place dispo au lieu d'être
   *  compactées. Passé par le parent quand TOUTES les cellules sont vides
   *  (évite le cluster à gauche sur une vue future sans activité). */
  stretchEmpty = input<boolean>(false);

  cellClick = output<PlanningCell>();
  eventClick = output<PlanningEvent>();

  expanded = signal(false);

  /** Largeur actuelle de la cellule. Pilote l'affichage du mois. */
  cellWidth = signal<number>(0);
  /** Affiche le mois quand la cellule est assez large (> 130px environ). */
  showMonth = computed(() => this.cellWidth() >= 130);

  /** True si le jour est un weekend ou un jour férié FR (grisé visuellement). */
  isCellOffDay = computed(() => isOffDay(this.cell().date));

  /**
   * Positions de l'overlay, avec fallback.
   * - Préférée : aligné en haut sur le top de l'origin (déploie vers le bas)
   * - Fallback 1 : aligné en bas sur le bottom de l'origin (déploie vers le haut)
   *   — utile quand le widget est en bas de page, évite d'être coupé
   * - Fallback 2 : centré verticalement (dernier recours si petite fenêtre)
   *
   * Le CDK essaie les positions dans l'ordre et choisit la première qui tient
   * dans le viewport. Centré horizontalement sur l'origin pour que les 2px de
   * border supplémentaire s'étalent symétriquement.
   */
  overlayPositions: ConnectedPosition[] = [
    {
      originX: "center",
      originY: "top",
      overlayX: "center",
      overlayY: "top",
    },
    {
      originX: "center",
      originY: "bottom",
      overlayX: "center",
      overlayY: "bottom",
    },
    {
      originX: "center",
      originY: "center",
      overlayX: "center",
      overlayY: "center",
    },
  ];

  private resizeObserver?: ResizeObserver;

  ngAfterViewInit(): void {
    const cardEl = this.host.nativeElement.querySelector(
      ".pl-list-cell",
    ) as HTMLElement | null;
    if (cardEl && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          this.cellWidth.set(entry.contentRect.width);
        }
      });
      this.resizeObserver.observe(cardEl);
      this.cellWidth.set(cardEl.getBoundingClientRect().width);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  @HostListener("window:resize")
  onResize(): void {
    if (this.expanded()) this.closeExpand();
  }
  @HostListener("window:scroll", ["$event"])
  onScroll(): void {
    if (this.expanded()) this.closeExpand();
  }

  /**
   * Label du jour.
   * - Cellule étroite : "Mer. 15"
   * - Cellule large (≥ 130px) : "Mer. 15 avr."
   */
  dayLabel = computed(() => {
    const fmt = this.showMonth() ? "EEE d MMM" : "EEE d";
    return format(this.cell().date, fmt, { locale: fr });
  });

  displayedEvents = computed(() => {
    const all = this.cell().events;
    return this.expanded() ? all : all.slice(0, this.maxVisible());
  });

  overflowCount = computed(() =>
    Math.max(0, this.cell().events.length - this.maxVisible()),
  );

  statusClass = computed(() => `pl-list-cell--${this.cell().status}`);

  eventTitle(event: PlanningEvent): string {
    return event.reference.label;
  }

  eventSubtitle(event: PlanningEvent): string {
    const p = event.payload as any;
    const parts: string[] = [];
    if (p?.numero) parts.push(p.numero);

    // Facture client en retard : libellé explicite "Retard règlement"
    // plutôt que le generique "EMISE" (ou pire "late") — sémantique
    // financière claire pour le comptable/commercial.
    if (event.source === "facture-client" && event.status === "late") {
      parts.push("Retard règlement");
    } else if (p?.etatLabel) {
      parts.push(p.etatLabel);
    }

    return parts.join(" · ") || event.status;
  }

  eventTooltip(event: PlanningEvent): string {
    const p = event.payload as any;
    const lines: string[] = [];

    // Ligne 1 : titre principal (ce que l'utilisateur identifie en un coup d'œil)
    // - OF (prod + PDC prépa/réinte) : nom du produit fabriqué
    // - Facture client : nom du client
    // - CF : nom du fournisseur
    // - CC / autres : label de référence (fallback)
    const title =
      (event.source === "of" && p?.article) ||
      (event.source === "facture-client" && p?.client) ||
      p?.fournisseur ||
      event.reference.label;
    if (title) lines.push(title);

    // Séparateur visuel (au lieu d'une vraie card HTML qui ajouterait du code)
    if (title) lines.push("————————");

    // Ligne de production — utile pour OF prod + PDC prépa/réinte (savoir
    // sur quelle ligne le produit sera fabriqué / préparé).
    if (event.source === "of" && p?.ligne) {
      lines.push(`Ligne : ${p.ligne}`);
    }

    // Identifiant + état compacts
    const idLine: string[] = [];
    if (p?.numero) idLine.push(p.numero);
    if (p?.etatLabel) idLine.push(p.etatLabel);
    if (idLine.length) lines.push(idLine.join(" · "));

    // Montant TTC pour factures client — info critique pour prioriser les
    // relances (on s'occupe d'abord de la grosse facture en retard).
    if (event.source === "facture-client" && p?.totalTTC != null) {
      const montant = new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
      }).format(p.totalTTC);
      lines.push(`Montant : ${montant}`);
    }

    // Event en retard : afficher la date initialement prévue + nb jours de retard
    // plutôt que la datePrevu migrée à today (sinon on perd l'info).
    // Formulation spécifique pour les factures (retard de RÈGLEMENT) vs
    // autres sources (retard d'action générique).
    if (event.status === "late" && p?.originalDatePrevu) {
      const dateLabel =
        event.source === "facture-client"
          ? "Échéance initiale"
          : "Prévu initialement";
      lines.push(
        `${dateLabel} : ${format(new Date(p.originalDatePrevu), "EEEE d MMMM yyyy", { locale: fr })}`,
      );
      if (p?.retardJours > 0) {
        const retardLabel =
          event.source === "facture-client"
            ? `Règlement en retard de ${p.retardJours} jour${p.retardJours > 1 ? "s" : ""}`
            : `En retard de ${p.retardJours} jour${p.retardJours > 1 ? "s" : ""}`;
        lines.push(retardLabel);
      }
    } else if (event.datePrevu) {
      const dateLabel =
        event.source === "facture-client" ? "Échéance" : "Prévu";
      lines.push(
        `${dateLabel} : ${format(new Date(event.datePrevu), "EEEE d MMMM yyyy", { locale: fr })}`,
      );
    }

    // Prépa PDC : afficher la date de démarrage prévue de l'OF (le J de la prépa
    // est calculé depuis cette date, c'est l'info contextuelle utile).
    if (p?.prepaPdc && p?.ofDateDebut) {
      lines.push(
        `Démarrage OF prévu : ${format(new Date(p.ofDateDebut), "EEEE d MMMM yyyy", { locale: fr })}`,
      );
    }

    if (event.dateReel)
      lines.push(
        `Réalisé : ${format(new Date(event.dateReel), "EEEE d MMMM yyyy", { locale: fr })}`,
      );
    return lines.join("\n");
  }

  /**
   * Classes CSS de la chip source : couleur par TYPE de source
   * (Client, Fournisseur, OF, PDC, Récep., Expé, Charg.) plus une classe
   * "late" si l'event est en retard pour ajouter une bordure rouge.
   */
  eventSourceClass(event: PlanningEvent): string {
    const sourceClass = `pl-list-cell__source--${this.sourceKey(event)}`;
    const lateClass =
      event.status === "late" ? "pl-list-cell__source--alert" : "";
    return `${sourceClass} ${lateClass}`.trim();
  }

  /** Clé courte unique pour le mapping CSS par type. */
  private sourceKey(event: PlanningEvent): string {
    if (event.source === "of") {
      if (event.kind === "pdc-prepa") return "pdc-prepa";
      if (event.kind === "pdc-reinte") return "pdc-reinte";
      return "of";
    }
    if (event.source === "expedition") {
      return event.kind === "chargement" ? "chargement" : "expedition";
    }
    return event.source; // commande, commande-client, dechargement, lot-mouvement
  }

  /**
   * Label court affiché dans la chip source (remplace le dot coloré).
   * Se base sur `source` + `kind` pour distinguer OF prod / OF PDC-prépa /
   * OF PDC-réinte, ou BL / chargement, etc.
   */
  eventSourceLabel(event: PlanningEvent): string {
    switch (event.source) {
      case "commande":
        return "Fournis.";
      case "commande-client":
        return "Client";
      case "of":
        if (event.kind === "pdc-prepa") return "Prépa PDC";
      if (event.kind === "pdc-reinte") return "Réinte PDC";
        return "OF";
      case "dechargement":
        return "Récep.";
      case "expedition":
        if (event.kind === "chargement") return "Charg.";
        return "Expé";
      case "facture-client":
        return "Fact.";
      case "lot-mouvement":
        return "Lot";
      default:
        return event.source;
    }
  }

  toggleExpand(evt: MouseEvent): void {
    if (this.cell().events.length > this.maxVisible()) {
      evt.stopPropagation();
      this.expanded.update((v) => !v);
    }
  }

  closeExpand(): void {
    this.expanded.set(false);
  }

  /**
   * Clic sur le corps de la cellule :
   * - Toggle l'état expand (ouverture/fermeture du focus overlay). Même si la
   *   cellule n'a pas d'overflow (tous les events déjà visibles), on ouvre
   *   l'overlay pour un "focus mode" cohérent.
   * - Émet `cellClick` pour que le parent puisse réagir (ex: filtrer la
   *   liste sous le planning par date).
   *
   * Les clics sur le chevron nav et les event-rows ont déjà un stopPropagation,
   * donc ils n'arrivent jamais ici.
   */
  onClick(): void {
    this.expanded.update((v) => !v);
    this.cellClick.emit(this.cell());
  }

  /** Click sur l'overlay (zone vide, pas sur un event ni le chevron) → ferme. */
  onOverlayClick(evt: MouseEvent): void {
    evt.stopPropagation();
    this.closeExpand();
  }

  onEventClick(event: PlanningEvent, evt: Event): void {
    evt.stopPropagation();
    this.eventClick.emit(event);
  }
}
